// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Table } from '@shared/ui-kit';
import { type ButtonExecuteTradesProps } from '@advisor-app/features/brokers';
import { type InitialSortBy } from '@advisor-app/features/ui/table';
import { exportTableToExcel } from '@shared/ui-kit/utils/export-table-to-excel';
import { type TradesOperations } from '@shared/data-access/interfaces/redux/trades';
import { type WorkflowAsset } from '@shared/data-access/interfaces/redux/workflow-state';
import {
  KpiBoxes,
  type KpiBoxesProps,
  SectionContainer,
} from '@shared/ui-kit/components';
import { useCallback, type ReactNode } from 'react';
import { useTrades } from '../hooks';
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { TradesActionToolbar } from './trades-action-toolbar';
import { type InternalProposalOperation } from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import { useTranslation } from 'react-i18next';
import { useAdvisorRole } from '@advisor-app/hooks/use-advisor-role';
export interface ColumnMethods {
  tickerMethod?: (ticker: string, index?: number) => Promise<void> | void;
  tickerOperation?: (
    ticker: string,
    operation: TradesOperations,
    index?: number,
  ) => void;
  onAccountChange?: (
    index: number,
    side: 'from' | 'to',
    iban: string,
    institution: { id: string; name: string },
  ) => void;
  handleDeleteTrade?: (ticker: string) => void;
  assetType?: WorkflowAsset;
  currency?: string;
  hasOnlyOneData?: boolean;
}

export interface TradesProps {
  tableData: { [key: string]: any }[];
  tableColumns: any;
  columnMethods?: ColumnMethods;
  kpisDataArray: KpiBoxesProps[];
  showExecuteTrades?: boolean;
  showTaxHarvesting?: boolean;
  showRebalance?: boolean;
  showFormAddTicker?: boolean;
  showMessageNoData?: boolean;
  messageNoData?: string;
  excelFileName?: string;
  showSendToTrader?: boolean;
  showSave?: boolean;
  showDownload?: boolean;
  showDeleteAllTrades?: boolean;
  showDownloadReport?: boolean;
  showTablePagination?: boolean;
  showSendProposals?: boolean;
  handleSendToTrader?: () => void;
  handleTaxHarvesting?: () => void;
  handleRebalance?: () => void;
  handleSavePortfolio?: () => void;
  handleDeleteAllTrades?: () => void;
  handleDownloadReport?: () => void;
  format?: 'trader' | 'default';
  backTo?: Paths;
  executeTrades?: ButtonExecuteTradesProps;
  initialSortBy?: InitialSortBy;
  disableTrader?: boolean;
  ['state-id']?: string;
  proposalOperations?: InternalProposalOperation[];
  className?: string;
  validProposal?: boolean;
  alerts?: ReactNode;
}

export const Trades = ({
  tableData,
  tableColumns,
  columnMethods,
  kpisDataArray,
  showExecuteTrades,
  showTaxHarvesting,
  showRebalance,
  showFormAddTicker,
  messageNoData,
  excelFileName,
  showSendToTrader,
  showSave,
  showDownload,
  showDeleteAllTrades,
  showDownloadReport,
  showTablePagination,
  showSendProposals,
  handleSendToTrader,
  handleTaxHarvesting,
  handleRebalance,
  handleSavePortfolio,
  handleDeleteAllTrades,
  handleDownloadReport,
  format = 'default',
  backTo,
  executeTrades,
  initialSortBy,
  disableTrader,
  'state-id': stateId,
  proposalOperations,
  className,
  validProposal = true,
  alerts,
}: TradesProps): JSX.Element => {
  const { traderExcelData, traderHeaderOrder } = useTrades({
    tableColumns,
    tableData,
    kpisDataArray,
  });

  const { t } = useTranslation(['common', 'components']);
  const onDownloadTrades = useCallback(
    () =>
      exportTableToExcel(
        format === 'trader' ? traderExcelData : tableData,
        excelFileName as string,
        {
          header: format === 'trader' ? traderHeaderOrder : [],
        },
      ),
    [format, traderExcelData, tableData, excelFileName, traderHeaderOrder],
  );

  const { canUseTrader, canSendProposals } = useAdvisorRole();

  return (
    <div data-testid="trades-content" className={className}>
      <TradesActionToolbar
        backTo={format !== 'trader' ? backTo : undefined}
        buttons={{
          executeTrades: showExecuteTrades,
          taxHarvesting: showTaxHarvesting,
          savePortfolio: showSave,
          downloadTrades: showDownload,
          downloadReport: showDownloadReport,
          trader: showSendToTrader && canUseTrader,
          rebalance: showRebalance,
          addTicker: showFormAddTicker && canUseTrader,
          deleteAllTrades: showDeleteAllTrades,
          sendProposals: showSendProposals && canSendProposals,
        }}
        onDownloadReport={handleDownloadReport}
        onTaxHarvesting={handleTaxHarvesting}
        onDownloadTrades={onDownloadTrades}
        onRebalance={handleRebalance}
        onSavePortfolio={handleSavePortfolio}
        onTrader={handleSendToTrader}
        executeTrades={executeTrades}
        onDeleteAllTrades={handleDeleteAllTrades}
        disableTrader={disableTrader}
        proposalOperations={proposalOperations}
        validProposal={validProposal}
      />

      {alerts}

      <div className="mb-4 grid grid-cols-4 gap-6">
        {kpisDataArray.map((vals, idx) => (
          <KpiBoxes key={idx} {...vals} />
        ))}
      </div>

      <SectionContainer className="!p-5">
        <div className="overflow-x-auto rounded">
          <Table.Grouped
            columns={tableColumns(columnMethods, t)}
            data={tableData}
            emptyState={messageNoData}
            initialSorting={initialSortBy}
            enablePagination={showTablePagination}
            state-id={stateId}
          />
        </div>
      </SectionContainer>
    </div>
  );
};
